import { ProviderDataType } from "@/api/ProviderApi";
import { importProviders } from "@/api/ProviderImportApi";
import { PROVIDER_IMPORT_FIELDS, REQUIRED_IMPORT_FIELDS, findProviderImportField } from "../providerImportFields";
import { ColumnMapping as ProviderColumnMapping } from "../providerImportMapping";
import { buildProviderImportPayload } from "../providerImportPayload";
import { loadExistingProviderData } from "../loadExistingProviderData";
import { resolveProviderRows } from "../resolveProviderRows";
import { suggestProviderColumnMapping } from "../suggestProviderColumnMapping";
import { PROVIDER_TYPES } from "@utils/Constants";
import { ImportDefinition } from "../core/types";

export type ProviderImportContext = {
    providers: ProviderDataType[];
};

/** Todo lo que el asistente de importacion necesita saber del modelo `Provider`. */
export const providerImportDefinition: ImportDefinition<ProviderDataType, ProviderImportContext> = {
    title: "Importar Proveedores",
    backUrl: "/user/providers",
    doneActionTitle: "Ir a la lista de proveedores",
    entityPlural: "proveedores",
    fileHelpText:
        "Seleccione el archivo de proveedores exportado de su sistema anterior. Se aceptan archivos CSV y Excel, con o sin fila de encabezados.",
    mappingHelpText:
        "Indique a que dato del proveedor corresponde cada columna. Las columnas que su sistema use y Easypos no, puede dejarlas sin asignar.",
    duplicateQuestion: "Si el proveedor ya existe (por codigo o identificacion):",
    errorRowsHelp: (errorCount, totalRows) =>
        `${errorCount} de ${totalRows} filas no se pueden importar porque no traen nombre de proveedor. Revise que la columna correcta este asignada al dato "Nombre"; si el archivo realmente no trae esos nombres, complete la columna antes de importar.`,
    doneHelpText:
        "El listado de proveedores se alimenta del buscador, que se actualiza unos segundos despues de la carga.",

    fields: PROVIDER_IMPORT_FIELDS,
    requiredFields: REQUIRED_IMPORT_FIELDS,
    findField: findProviderImportField,
    suggestMapping: suggestProviderColumnMapping,

    loadContext: loadExistingProviderData,

    resolveRows: ({ dataRows, mapping, context, policy }) =>
        resolveProviderRows({
            dataRows,
            mapping: mapping as ProviderColumnMapping,
            existingProviders: context.providers,
            policy,
        }),

    // `Provider` no tiene ninguna entidad relacionada que haya que crear por nombre.
    missingLookups: () => null,

    previewColumns: [
        { header: "Codigo", render: (row) => row.data.codigo },
        { header: "Nombre", render: (row) => row.data.nombre },
        { header: "Identificacion", render: (row) => row.data.identificacion },
        { header: "Tipo", render: (row) => PROVIDER_TYPES[`${row.data.tipo ?? 0}`] },
        { header: "Telefono 1", render: (row) => row.data.telefono1 },
    ],

    commit: async ({ resolved, onProgress }) => {
        const payload = buildProviderImportPayload(resolved.rows);
        const skippedCount = payload.filter((row) => row.action === "skip").length;

        const summary = await importProviders(payload, onProgress);

        return { summary, skippedCount };
    },
};

export default providerImportDefinition;
